import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import PersonaShell from './persona-shell';
import Spinner from '../../components/common/spinner';
import {
  usePersona,
  useUpdatePersona,
  usePublishPersona,
  useUnpublishPersona,
  useUploadCorpus,
  useCorpusChunks,
  useDeleteCorpusChunk,
} from '../../hooks/usePersonaAdmin';

interface MetadataFormValues {
  display_name: string;
  scope_line: string;
  welcome_message: string;
  avatar_file_path: string;
}

const PAGE_SIZE = 25;

const PersonaOverviewPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const [chunkPage, setChunkPage] = useState(1);
  const { data: persona, isLoading } = usePersona(personaId);
  const processingActive = persona?.processing_status === 'processing';
  // Same query key — enables polling only while ingest is running.
  usePersona(personaId, { refetchInterval: processingActive ? 2000 : false });
  const updatePersona = useUpdatePersona(personaId);
  const publish = usePublishPersona(personaId);
  const unpublish = useUnpublishPersona(personaId);
  const uploadCorpus = useUploadCorpus(personaId);
  const deleteChunk = useDeleteCorpusChunk(personaId);

  const { data: chunks, isFetching: chunksFetching } = useCorpusChunks(personaId, chunkPage, PAGE_SIZE, {
    refetchInterval: processingActive ? 2000 : false,
  });

  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInput: React.RefObject<HTMLInputElement> = createRef();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty, isSubmitting },
  } = useForm<MetadataFormValues>({
    defaultValues: {
      display_name: '',
      scope_line: '',
      welcome_message: '',
      avatar_file_path: '',
    },
  });

  useEffect(() => {
    if (persona) {
      reset({
        display_name: persona.display_name || '',
        scope_line: persona.scope_line || '',
        welcome_message: persona.welcome_message || '',
        avatar_file_path: persona.avatar_file_path || '',
      });
      setAvatarPreview(persona.image_url || persona.avatar_file_path || '');
    }
  }, [persona, reset]);

  const watchedAvatar = watch('avatar_file_path');

  const onAvatarFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Avatar must be an image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        if (img.width !== img.height) {
          toast.error('Avatar must be square (equal height and width)');
          return;
        }
        const dataUrl = e.target.result as string;
        setValue('avatar_file_path', dataUrl, { shouldDirty: true });
        setAvatarPreview(dataUrl);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const onFileChange = (event: React.FormEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) onAvatarFile(file);
  };

  const onSubmit = handleSubmit(async (values) => {
    await updatePersona.mutateAsync({
      display_name: values.display_name.trim(),
      scope_line: values.scope_line.trim(),
      welcome_message: values.welcome_message.trim(),
      avatar_file_path: values.avatar_file_path.trim(),
    });
  });

  const handleCorpusFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      for (const file of Array.from(files)) {
        if (!/\.(csv|txt)$/i.test(file.name)) {
          toast.error(`${file.name}: only CSV/TXT files are supported`);
          continue;
        }
        await uploadCorpus.mutateAsync(file);
      }
    },
    [uploadCorpus],
  );

  const onCorpusInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleCorpusFiles(e.target.files);
    e.target.value = '';
  };

  const onCorpusDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleCorpusFiles(e.dataTransfer.files);
  };

  const chunkRows = chunks?.data || [];
  const chunkTotal = chunks?.count || 0;
  const totalPages = Math.max(1, Math.ceil(chunkTotal / PAGE_SIZE));

  const isOn = persona?.status === 'on';

  const publishAction = useMemo(() => {
    if (isOn) {
      return (
        <button
          className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          disabled={unpublish.isLoading}
          onClick={() => unpublish.mutate()}
        >
          {unpublish.isLoading ? 'Unpublishing…' : 'Unpublish'}
        </button>
      );
    }
    return (
      <button
        className="rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
        disabled={publish.isLoading || persona?.processing_status !== 'ready'}
        title={persona?.processing_status !== 'ready' ? 'Corpus must be ready before publishing' : ''}
        onClick={() => publish.mutate()}
      >
        {publish.isLoading ? 'Publishing…' : 'Publish'}
      </button>
    );
  }, [isOn, publish, unpublish, persona?.processing_status]);

  return (
    <PersonaShell personaId={personaId} tab="overview">
      {isLoading && !persona ? (
        <Spinner alignmentClass="flex items-center justify-center py-16" />
      ) : (
        <div className="mx-auto max-w-5xl space-y-8 p-8">
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Metadata</h2>
                <p className="text-xs text-gray-500">
                  Internal name: <span className="font-mono">{persona?.name}</span> · Listener role:{' '}
                  <span className="font-mono">{persona?.listener_role_id ?? '—'}</span>
                </p>
              </div>
              {publishAction}
            </div>
            <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-1">
                <label htmlFor="display_name" className="mb-1 block text-sm font-medium text-gray-700">
                  Display Name
                </label>
                <input
                  id="display_name"
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  {...register('display_name')}
                />
              </div>
              <div className="md:col-span-1">
                <label htmlFor="scope_line" className="mb-1 block text-sm font-medium text-gray-700">
                  Scope Line
                </label>
                <input
                  id="scope_line"
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  placeholder="One-line description of what this Peersona covers"
                  {...register('scope_line')}
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="welcome_message" className="mb-1 block text-sm font-medium text-gray-700">
                  Welcome Message
                </label>
                <textarea
                  id="welcome_message"
                  rows={3}
                  className="w-full rounded border border-gray-300 p-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  {...register('welcome_message')}
                />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">Avatar</label>
                <p className="mb-2 text-xs text-gray-500">
                  Must be square. Uploads are stored as base64 until the metadata is saved.
                </p>
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-40 w-40 items-center justify-center rounded-md bg-gray-100 ${
                      !avatarPreview && 'cursor-pointer'
                    }`}
                    onClick={() => !avatarPreview && fileInput.current?.click()}
                  >
                    {avatarPreview ? (
                      <img className="h-full w-full rounded-md object-cover" src={avatarPreview} alt="Persona avatar" />
                    ) : (
                      <span className="text-3xl text-gray-400">+</span>
                    )}
                    <input
                      ref={fileInput}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFileChange}
                      onClick={() => {
                        if (fileInput.current) fileInput.current.value = '';
                      }}
                    />
                  </div>
                  {watchedAvatar && (
                    <button
                      type="button"
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      onClick={() => {
                        setAvatarPreview('');
                        setValue('avatar_file_path', '', { shouldDirty: true });
                      }}
                    >
                      Remove avatar
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 md:col-span-2">
                <button
                  type="button"
                  onClick={() => reset()}
                  disabled={!isDirty}
                  className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={!isDirty || isSubmitting || updatePersona.isLoading}
                  className="bg-blue-primary rounded px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {updatePersona.isLoading ? 'Saving…' : 'Save Metadata'}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Corpus</h2>
                <p className="text-xs text-gray-500">
                  Processing status: <span className="font-mono">{persona?.processing_status || 'pending'}</span> ·
                  Chunks: <span className="font-mono">{persona?.chunk_count ?? chunkTotal}</span>
                </p>
              </div>
            </div>

            {processingActive && (
              <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-4">
                <div className="mb-1 flex items-center justify-between text-sm font-medium text-blue-900">
                  <span>Ingest in progress</span>
                  <span className="font-mono text-xs">
                    {persona?.processing_progress?.completed ?? persona?.chunk_count ?? 0}
                    {' / '}
                    {persona?.processing_progress?.total ?? '…'}
                  </span>
                </div>
                <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-blue-100">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all duration-500"
                    style={{
                      width: `${
                        persona?.processing_progress?.total
                          ? Math.min(
                              100,
                              Math.round(
                                ((persona.processing_progress.completed || 0) / persona.processing_progress.total) *
                                  100,
                              ),
                            )
                          : 8
                      }%`,
                    }}
                  />
                </div>
                <p className="text-xs text-blue-800">
                  {persona?.processing_progress?.message ||
                    'Embedding and saving chunks… this page refreshes every few seconds.'}
                </p>
              </div>
            )}

            {persona?.processing_status === 'failed' && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <div className="font-medium">Corpus ingest failed</div>
                <p className="mt-1 text-xs">
                  {persona?.processing_progress?.error || 'Re-upload the CSV/TXT to retry. Check API logs for details.'}
                </p>
              </div>
            )}

            <div
              className={`flex flex-col items-center justify-center rounded-md border-2 border-dashed p-8 text-sm text-gray-600 ${
                isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onCorpusDrop}
            >
              <p className="mb-2">Drop CSV or TXT files here, or</p>
              <label className="bg-blue-primary cursor-pointer rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
                Choose files
                <input
                  type="file"
                  multiple
                  accept=".csv,.txt,text/csv,text/plain"
                  className="hidden"
                  onChange={onCorpusInput}
                />
              </label>
              {uploadCorpus.isLoading && <p className="mt-3 text-xs text-gray-500">Uploading…</p>}
            </div>

            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Chunk Browser {chunksFetching ? '(refreshing…)' : ''}
                </h3>
                <div className="text-xs text-gray-500">
                  Page {chunkPage} of {totalPages} · {chunkTotal} chunks
                </div>
              </div>
              <table className="w-full table-fixed border-collapse text-sm">
                <thead>
                  <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="w-20 py-2 pr-2">ID</th>
                    <th className="py-2 pr-2">Content</th>
                    <th className="w-36 py-2 pr-2">Source</th>
                    <th className="w-24 py-2 pr-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {chunkRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-xs text-gray-400">
                        No corpus chunks yet.
                      </td>
                    </tr>
                  )}
                  {chunkRows.map((chunk) => (
                    <tr key={chunk.id} className="border-b align-top">
                      <td className="py-2 pr-2 font-mono text-xs text-gray-500">{chunk.id}</td>
                      <td className="py-2 pr-2">
                        <div className="max-h-24 overflow-hidden whitespace-pre-wrap text-xs text-gray-700">
                          {chunk.content_preview || chunk.content}
                        </div>
                      </td>
                      <td className="py-2 pr-2 text-xs text-gray-500">{chunk.source_type || '—'}</td>
                      <td className="py-2 pr-2 text-right">
                        <button
                          className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                          disabled={deleteChunk.isLoading}
                          onClick={() => {
                            if (window.confirm('Delete this chunk?')) deleteChunk.mutate(chunk.id);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="mt-3 flex justify-end gap-2 text-xs">
                  <button
                    className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    disabled={chunkPage <= 1}
                    onClick={() => setChunkPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    className="rounded border border-gray-300 px-3 py-1 hover:bg-gray-50 disabled:opacity-50"
                    disabled={chunkPage >= totalPages}
                    onClick={() => setChunkPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </PersonaShell>
  );
};

export default PersonaOverviewPage;
